import { useState, useEffect } from 'react'
import { FINGERPRINT_STORAGE_KEY, useLocalStorage } from '../Utils/useLocalStorage'
import { getFingerprint } from '../services/fingerPrint/getClientFingerprint.service'

/**
 * Hook to manage the client's fingerprint with localStorage caching
 * @returns {Object} An object containing the fingerprint and loading state
 * @returns {string} returns.fingerprint - The client's unique fingerprint
 * @returns {boolean} returns.isLoading - Whether the fingerprint is being generated
 * @returns {Error | null} returns.error - Any error that occurred while generating the fingerprint
 * 
 * @example
 * const { fingerprint, isLoading, error } = useFingerprint();
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 * return <div>Your Fingerprint: {fingerprint}</div>;
 */
export function useFingerprint() {
  const [storedFingerprint, setStoredFingerprint] = useLocalStorage<string>(FINGERPRINT_STORAGE_KEY, '')
  const [error, setError] = useState<Error | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isMounted = true

    const generateFingerprint = async () => {
      // Si nous avons déjà un fingerprint, ne rien faire
      if (storedFingerprint) {
        if (isMounted) {
          setIsLoading(false)
        }
        return
      }

      try {
        const fingerprint = await getFingerprint()

        if (!fingerprint) {
          throw new Error('No fingerprint generated')
        }

        if (isMounted) {
          setStoredFingerprint(fingerprint)
          setError(null)
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Failed to generate fingerprint'))
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    generateFingerprint()

    return () => {
      isMounted = false
    }
  }, []) // Empty dependency array since we only want to run this effect once

  return { fingerprint: storedFingerprint, error, isLoading }
} 
